import { useParams } from 'react-router'
import { Typography } from '../common/typography'
import { useRaceStatsDetails } from './api'
import { AspectRatio, Image, Table, TextInput } from '@mantine/core'
import classes from './Races.module.css'
import clsx from 'clsx'
import { useMediaQuery } from '@mantine/hooks'
import { useMemo, useState } from 'react'
import { FmtColored } from '../widgets/format'
import { getRankSuffix } from '../common/helpers'
import {
  IconArrowDown,
  IconArrowUp,
  IconPoint,
  IconZoom,
} from '@tabler/icons-react'

export default function RaceSummary() {
  const { competitionId, raceId } = useParams()
  const { data: raceStats } = useRaceStatsDetails({
    competition_id: Number(competitionId),
    race_id: Number(raceId),
  })

  const isMobile = useMediaQuery('(max-width: 800px)')
  const [searchValue, setSearchValue] = useState<string>('')

  const filteredResults = useMemo(() => {
    let result = raceStats.results

    if (searchValue.trim()) {
      result = result.filter((team) =>
        team.team_name.toLowerCase().includes(searchValue.toLowerCase())
      )
    }

    return result
  }, [searchValue, raceStats.results])

  const kpis = useMemo(() => {
    if (filteredResults.length > 0 && filteredResults[0].kpis) {
      return raceStats.results[0].kpis
    }
    return []
  }, [raceStats.results])

  const rows = filteredResults.map((result) => {
    const change = result.rank_change

    return (
      <Table.Tr key={result.team_id}>
        <Table.Td>
          <div className={classes.rankColumn}>
            <Typography lh="150" ta="left">
              {result.rank}
              <sup>{getRankSuffix(result.rank)}</sup>
            </Typography>
            {change === 0 && (
              <div className={classes.rankUpdate}>
                <IconPoint className={classes.rankIcon} />
              </div>
            )}
            {change > 0 && (
              <div className={classes.rankUpdate}>
                <IconArrowUp
                  className={clsx(classes.rankIcon, classes.rankUp, {
                    [classes.rankIconMobile]: isMobile,
                  })}
                />
                <Typography
                  fs="sm"
                  ff="JetBrains Mono"
                  fw={500}
                  ta="center"
                  className={classes.rankUp}
                >
                  {`+${change}`}
                </Typography>
              </div>
            )}
            {change < 0 && (
              <div className={classes.rankUpdate}>
                <IconArrowDown
                  className={clsx(classes.rankIcon, classes.rankDown, {
                    [classes.rankIconMobile]: isMobile,
                  })}
                />
                <Typography
                  fs="sm"
                  ff="JetBrains Mono"
                  fw={500}
                  ta="center"
                  className={classes.rankDown}
                >
                  {change}
                </Typography>
              </div>
            )}
          </div>
        </Table.Td>
        <Table.Td>
          <div className={classes.teamCell}>
            <AspectRatio ratio={1 / 1}>
              <Image
                src={result.team_logo.mini}
                alt={result.team_name}
                className={clsx(classes.teamLogo, {
                  [classes.teamLogoMobile]: isMobile,
                })}
                fit="scale-down"
              />
            </AspectRatio>
            <Typography lh="150" className={classes.teamName}>
              {result.team_name}
            </Typography>
          </div>
        </Table.Td>
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="150" ta="center">
            {result.race_points}
          </Typography>
        </Table.Td>
        {result.kpis.map((kpi) => (
          <Table.Td key={kpi.metric_key}>
            <Typography ff="JetBrains Mono" lh="150" ta="center">
              <FmtColored
                value={kpi.percent}
                metric={kpi.metric_key}
                component="percent"
                statistic="value"
              />
            </Typography>
          </Table.Td>
        ))}
        <Table.Td>
          <Typography ff="JetBrains Mono" lh="150" ta="center">
            {result.race_rating.toFixed(2)}
          </Typography>
        </Table.Td>
        <Table.Td>
          <div className={classes.formWrapper}>
            {result.form.slice(0, 3).map((form, index) => (
              <div
                key={form + index}
                className={clsx(
                  classes.formContainer,
                  { [classes.firstPlace]: Number(form) === 1 },
                  { [classes.secondPlace]: Number(form) === 2 },
                  { [classes.thirdPlace]: Number(form) === 3 }
                )}
              >
                <Typography
                  fs="xs"
                  fw={600}
                  fc={{ light: 'white', dark: 'white' }}
                  lh="130"
                  ta="center"
                >
                  {form}
                  <sup>{getRankSuffix(Number(form))}</sup>
                </Typography>
              </div>
            ))}
          </div>
        </Table.Td>
      </Table.Tr>
    )
  })

  return (
    <div className={classes.tableContainer}>
      {!isMobile && (
        <div className={clsx(classes.filtersContainer, classes.filtersSummary)}>
          <TextInput
            placeholder="Search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.currentTarget.value)}
            rightSection={<IconZoom className={classes.zoomIcon} />}
            rightSectionPointerEvents="none"
            size="xs"
            classNames={{
              input: classes.searchInput,
              section: classes.searchSection,
            }}
          />
        </div>
      )}
      <Table.ScrollContainer minWidth={300}>
        <Table
          classNames={{
            table: clsx(classes.table, isMobile && classes.stickyFirstColumn),
            tr: classes.tr,
            th: classes.th,
            td: classes.td,
          }}
          verticalSpacing="xs"
          withRowBorders={false}
        >
          <Table.Thead>
            <Table.Tr className={classes.headerRow}>
              <Table.Th>
                <Typography
                  fs="sm"
                  fw={600}
                  fc={{ light: 'gray-9', dark: 'gray-9' }}
                  lh="140"
                >
                  #
                </Typography>
              </Table.Th>
              <Table.Th>
                <Typography
                  fs="sm"
                  fw={600}
                  fc={{ light: 'gray-9', dark: 'gray-9' }}
                  lh="140"
                >
                  Team
                </Typography>
              </Table.Th>
              <Table.Th>
                <Typography
                  fs="sm"
                  fw={600}
                  fc={{ light: 'gray-9', dark: 'gray-9' }}
                  lh="140"
                  ta="center"
                >
                  Race Pts
                </Typography>
              </Table.Th>
              {kpis.map((kpi) => (
                <Table.Th key={kpi.metric_key}>
                  <Typography
                    fs="sm"
                    fw={600}
                    fc={{ light: 'gray-9', dark: 'gray-9' }}
                    lh="140"
                    ta="center"
                  >
                    {kpi.metric_name}
                  </Typography>
                </Table.Th>
              ))}
              <Table.Th>
                <Typography
                  fs="sm"
                  fw={600}
                  fc={{ light: 'gray-9', dark: 'gray-9' }}
                  lh="140"
                  ta="center"
                >
                  Race Rating
                </Typography>
              </Table.Th>
              <Table.Th>
                <Typography
                  fs="sm"
                  fw={600}
                  fc={{ light: 'gray-9', dark: 'gray-9' }}
                  lh="140"
                  ta="center"
                >
                  Form
                </Typography>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows}
            {/* {filteredResults.length > 0 ? (
              rows
            ) : searchValue ? (
              <Table.Tr>
                <Table.Td colSpan={5 + kpis.length}>
                  <Typography ta="center">
                    No teams found matching "{searchValue}"
                  </Typography>
                </Table.Td>
              </Table.Tr>
            ) : null} */}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </div>
  )
}
